import type { CreatorVerificationProof, ReactiveWorldsMapPackage } from "../types";
import { idbDelete, idbGet, idbGetAll, idbSet } from "../core/indexedDb";
import { mapRevisionHash } from "../utils/mapRevision";
import { attestCreatorCompletion } from "../services/creatorVerificationRepository";
export interface ProjectVerification {
  revisionHash: string;
  verifiedAt: string;
  physicsVersion: string;
  verificationId?: string;
  authority?: "server" | "local";
}
export interface LocalProject {
  id: string;
  title: string;
  package: ReactiveWorldsMapPackage;
  createdAt: string;
  updatedAt: string;
  sourceMapId?: string;
  remixOf?: string;
  publishedAt?: string;
  publishedMapId?: string;
  verification?: ProjectVerification;
}
const uid = () =>
  `project_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
export interface DraftCleanupSettings {
  enabled: boolean;
  graceDays: number;
}
const CLEANUP_KEY = "rw_draft_cleanup_v1";
export const DEFAULT_DRAFT_CLEANUP: DraftCleanupSettings = {
  enabled: true,
  graceDays: 7,
};
export function getDraftCleanupSettings(): DraftCleanupSettings {
  try {
    const value = JSON.parse(localStorage.getItem(CLEANUP_KEY) || "null") as Partial<DraftCleanupSettings> | null;
    return {
      enabled: value?.enabled !== false,
      graceDays: Math.min(90, Math.max(1, Math.round(Number(value?.graceDays) || 7))),
    };
  } catch {
    return DEFAULT_DRAFT_CLEANUP;
  }
}
export function setDraftCleanupSettings(settings: DraftCleanupSettings) {
  const normalized = {
    enabled: settings.enabled,
    graceDays: Math.min(90, Math.max(1, Math.round(Number(settings.graceDays) || 7))),
  };
  localStorage.setItem(CLEANUP_KEY, JSON.stringify(normalized));
  return normalized;
}
export const draftLifetimeMs = (days: number) =>
  Math.min(90, Math.max(1, Math.round(Number(days) || 7))) * 24 * 60 * 60 * 1000;

function normalizeProjectBranding(project: LocalProject): LocalProject {
  const defaultTitle = project.title.trim().toLowerCase() === "untitled map",
    packageTitle = String(project.package.ugc.title || "").trim().toLowerCase() === "untitled map";
  if (!defaultTitle && !packageTitle) return project;
  return {
    ...project,
    title: defaultTitle ? "Untitled Maze" : project.title,
    package: packageTitle
      ? { ...project.package, ugc: { ...project.package.ugc, title: "Untitled Maze" } }
      : project.package,
  };
}

function isUnusedBlank(project: LocalProject) {
  if (project.publishedAt || !["untitled map", "untitled maze"].includes(project.title.trim().toLowerCase())) return false;
  return project.package.cells.every((row) =>
    row.every((cell) => !cell || Object.keys(cell).length === 0),
  );
}
export async function createProject(
  pkg: ReactiveWorldsMapPackage,
  meta: Partial<LocalProject> = {},
): Promise<LocalProject> {
  const now = new Date().toISOString(),
    p: LocalProject = {
      id: uid(),
      title: String(pkg.ugc.title || "Untitled Maze"),
      package: structuredClone(pkg),
      createdAt: now,
      updatedAt: now,
      ...meta,
    };
  await idbSet("projects", p.id, p);
  return p;
}
export async function loadProject(id: string): Promise<LocalProject | null> {
  const project = await idbGet<LocalProject>("projects", id);
  return project ? normalizeProjectBranding(project) : null;
}
export async function listProjects(
  settings = getDraftCleanupSettings(),
): Promise<LocalProject[]> {
  const all = (await idbGetAll<LocalProject>("projects")).map(normalizeProjectBranding),
    drafts = all.filter((project) => !project.publishedAt),
    ordered = drafts.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  if (!settings.enabled) return ordered;
  const cutoff = Date.now() - draftLifetimeMs(settings.graceDays),
    blankIds = new Set(
      ordered.filter(isUnusedBlank).slice(1).map((project) => project.id),
    ),
    expiredIds = new Set(
      ordered
        .filter((project) => {
          const stamp = Date.parse(project.updatedAt || project.createdAt);
          return Number.isFinite(stamp) && stamp < cutoff;
        })
        .map((project) => project.id),
    ),
    removeIds = new Set([...blankIds, ...expiredIds]);
  if (removeIds.size)
    await Promise.all([...removeIds].map((id) => idbDelete("projects", id)));
  return ordered.filter((project) => !removeIds.has(project.id));
}
export async function deleteProject(id: string): Promise<void> {
  await idbDelete("projects", id);
}
export async function saveProject(
  id: string,
  pkg: ReactiveWorldsMapPackage,
): Promise<LocalProject> {
  const prior = await loadProject(id),
    now = new Date().toISOString();
  const next: LocalProject = prior
    ? {
        ...prior,
        title: String(pkg.ugc.title || prior.title),
        package: structuredClone(pkg),
        verification:
          prior.verification?.revisionHash === (await mapRevisionHash(pkg))
            ? prior.verification
            : undefined,
        publishedAt: undefined,
        publishedMapId: undefined,
        updatedAt: now,
      }
    : {
        id,
        title: String(pkg.ugc.title || "Untitled Maze"),
        package: structuredClone(pkg),
        createdAt: now,
        updatedAt: now,
      };
  await idbSet("projects", id, next);
  return next;
}
export async function markProjectPublished(id: string, mapId: string) {
  const prior = await loadProject(id);
  if (!prior) return;
  await idbSet("projects", id, {
    ...prior,
    publishedAt: new Date().toISOString(),
    publishedMapId: mapId,
  });
}
export async function markProjectVerified(
  id: string,
  pkg: ReactiveWorldsMapPackage,
  proof: CreatorVerificationProof | null,
): Promise<LocalProject> {
  const prior = await loadProject(id);
  if (!prior) throw new Error("Draft project not found.");
  if (!proof) throw new Error("The runtime did not produce a replay proof.");
  const revisionHash = await mapRevisionHash(pkg),
    attestation = await attestCreatorCompletion(id, pkg, proof);
  if (!attestation) throw new Error("Server verification is unavailable.");
  const verifiedAt = attestation.verifiedAt,
    next: LocalProject = {
      ...prior,
      title: String(pkg.ugc.title || prior.title),
      package: structuredClone(pkg),
      updatedAt: verifiedAt,
      verification: {
        revisionHash,
        verifiedAt,
        physicsVersion: pkg.engineVersion || pkg.version,
        verificationId: attestation?.verificationId,
        authority: "server",
      },
    };
  if (attestation.revisionHash !== revisionHash)
    throw new Error("The server verified a different gameplay revision.");
  await idbSet("projects", id, next);
  return next;
}
export async function isProjectVerified(project: LocalProject) {
  return !!project.verification &&
    project.verification.revisionHash === await mapRevisionHash(project.package);
}
export function blankMapPackage(): ReactiveWorldsMapPackage {
  return {
    schema: "reactive-worlds-ugc",
    version: "8.24",
    schemaVersion: 2,
    engineVersion: "8.24",
    cols: 20,
    rows: 30,
    theme: "depot",
    seed: Math.floor(Math.random() * 2147483647),
    ugc: {
      title: "Untitled Maze",
      levelType: "Puzzle",
      objective: "gold_exit",
      buildStyle: "none",
      buildZonesEnabled: false,
      inventory: [],
      liveLogicColors: "visible",
      nextDeviceNo: {},
    },
    cells: Array.from({ length: 30 }, () =>
      Array.from({ length: 20 }, () => ({})),
    ),
  };
}
